import React from 'react';
import { useSelector } from 'react-redux';
import { GroupFinalCalculation } from '~/Api/User/ProductGroup/CzReimbursement';
import { EmptyResultReason } from '~/CzReimbursement/EmptyResultReason';
import { selectGroup } from '~/CzReimbursement/reducer';
import { State } from '~/Store';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading';

import styles from './styles.less';

const NewReimbursementPerUTDD: React.FunctionComponent<
	React.PropsWithChildren<{
		finalCalculation: GroupFinalCalculation;
	}>
> = ({ finalCalculation }) => {
	if (finalCalculation.baseReimbursement === null) {
		return null;
	}

	switch (finalCalculation.baseReimbursement.finalPrice) {
		case 'lowest-ex-factory':
			return <>{finalCalculation.baseReimbursement.lowestExFactoryPerUTDD?.roundedHumanFormat()}</>;
		case 'average-of-2nd-and-3rd-lowest-ex-factory':
			return (
				<>{finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD?.roundedHumanFormat()}</>
			);
		default:
			return null;
	}
};

export const GroupCalculation: React.FunctionComponent<React.PropsWithChildren<{ groupId: string }>> = ({
	groupId,
}) => {
	const czReimbursement = useSelector((state: State) => selectGroup(state.czReimbursement, groupId));
	if (czReimbursement === null || czReimbursement.group === null) {
		return null;
	}

	const { finalCalculation } = czReimbursement.group;

	return (
		<div className={styles.container}>
			<div className={styles.columns}>
				<div className={styles.leftColumn}>
					<Heading level={3}>Lowest ex-factory prices (reference product):</Heading>

					{finalCalculation.lowestPrices.map((lowestPrice, i) => (
						<div className={styles.row} key={lowestPrice.id}>
							<span className={styles.number}>{i + 1}.</span>
							<span className={styles.countryIcon}>
								<CountryIcon code={lowestPrice.dataSource.country.alpha2} name={lowestPrice.dataSource.country.name} />
							</span>

							<span className={styles.countryDescription}>
								{lowestPrice.dataSource.country.alpha2}
								<br />
								{lowestPrice.dataSource.shortName}
							</span>

							<span className={styles.lowestPriceValue}>{lowestPrice.exFactoryPrice.roundedHumanFormat()}</span>
						</div>
					))}
				</div>
				<div className={styles.rightColumn}>
					<Heading level={3}>Lowest price per ODTD:</Heading>

					<div className={styles.row}>
						<p className={styles.rowName}>Reference product:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement?.referenceProduct?.packName}
							{', '}
							{finalCalculation.baseReimbursement?.referenceProduct?.suklCode}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Lowest ex-factory price per ODTD:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement !== null ? (
								finalCalculation.baseReimbursement.finalPrice === 'lowest-ex-factory' ? (
									<strong>{finalCalculation.baseReimbursement.lowestExFactoryPerUTDD?.roundedHumanFormat()}</strong>
								) : (
									finalCalculation.baseReimbursement.lowestExFactoryPerUTDD?.roundedHumanFormat()
								)
							) : null}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Average of 2nd and 3rd lowest ex-factory price per ODTD:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement !== null ? (
								finalCalculation.baseReimbursement.finalPrice === 'average-of-2nd-and-3rd-lowest-ex-factory' ? (
									<strong>
										{finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD?.roundedHumanFormat()}
									</strong>
								) : (
									finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD?.roundedHumanFormat()
								)
							) : null}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Deviation:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement !== null &&
							finalCalculation.baseReimbursement.deviationPercentage !== null
								? `${finalCalculation.baseReimbursement.deviationPercentage}%`
								: null}
						</p>
					</div>

					{finalCalculation.emptyResultReason.length > 0 ? (
						<div className={styles.rowError}>
							{finalCalculation.emptyResultReason.map((error) => (
								<EmptyResultReason error={error} key={error.code} />
							))}
						</div>
					) : null}

					<Heading level={3}>Possible savings</Heading>

					<div className={styles.row}>
						<p className={styles.rowName}>
							Spending per group per
							{finalCalculation.possibleSavings.spendingPerGroup !== null
								? ` ${finalCalculation.possibleSavings.spendingPerGroup.year}`
								: null}
							:
						</p>
						<p className={styles.rowValue}>
							{finalCalculation.possibleSavings.spendingPerGroup?.price.roundedHumanFormat()}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>EKV1 (Current reimbursement per ODTD):</p>
						<p className={styles.rowValue}>
							{finalCalculation.possibleSavings.currentReimbursementPerUTDD?.price.roundedHumanFormat()}
						</p>
					</div>

					{finalCalculation.possibleSavings.currentReimbursementPerUTDD?.sameValueInAllLists === false ? (
						<div className={styles.row}>
							<p className={styles.differentEkvWarning}>
								There are products with different EKV1 in the group. The most frequent value is displayed.
							</p>
						</div>
					) : null}

					<div className={styles.row}>
						<p className={styles.rowName}>New reimbursement per ODTD:</p>
						<p className={styles.rowValue}>
							<NewReimbursementPerUTDD finalCalculation={finalCalculation} />
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Possible savings:</p>
						<p className={styles.rowValue}>
							{finalCalculation.possibleSavings.possibleSavingsPercentage !== null
								? `${finalCalculation.possibleSavings.possibleSavingsPercentage}%`
								: null}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
